import { useEffect, useState } from 'react'
import { motion } from 'framer-motion'
import { useTranslation } from 'react-i18next'
import { type FeedFilter, type GenderFilter, getFeedFilters, updateFeedFilters } from '../lib/api'
import RangeSlider from './RangeSlider'

interface FeedFilterSheetProps {
  isAdmin?: boolean
  onClose: () => void
  onSaved: () => void
}

const GENDER_OPTION_KEYS: { value: GenderFilter; labelKey: string }[] = [
  { value: 'any', labelKey: 'filters.genderAny' },
  { value: 'male', labelKey: 'filters.genderMale' },
  { value: 'female', labelKey: 'filters.genderFemale' },
]

const RADIUS_PRESETS = [5, 10, 25, 50, 100, 0]

export default function FeedFilterSheet({ isAdmin = false, onClose, onSaved }: FeedFilterSheetProps) {
  const { t } = useTranslation()
  const [filter, setFilter] = useState<FeedFilter | null>(null)
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)

  // Load existing filter on mount.
  useEffect(() => {
    getFeedFilters()
      .then(f => {
        setFilter(f)
        setLoading(false)
      })
      .catch(() => {
        // Fall back to defaults.
        setFilter({ age_min: 18, age_max: 100, gender: 'any', radius_km: 0 })
        setLoading(false)
      })
  }, [])

  async function handleSave() {
    if (!filter) return
    setSaving(true)
    setError(null)
    try {
      await updateFeedFilters(filter)
      onSaved()
    } catch {
      setError(t('filters.saveError'))
    } finally {
      setSaving(false)
    }
  }

  return (
    <div
      className="absolute inset-0 z-20 flex flex-col justify-end bg-black/50"
      onClick={onClose}
    >
      <motion.div
        className="max-h-[85dvh] overflow-y-auto rounded-t-2xl bg-tg-bg"
        initial={{ y: '100%' }}
        animate={{ y: 0 }}
        exit={{ y: '100%' }}
        transition={{ type: 'spring', stiffness: 300, damping: 30 }}
        onClick={e => e.stopPropagation()}
      >
        <div className="px-5 pb-8 pt-5">
          <p className="mb-5 text-lg font-semibold text-tg-text">{t('filters.title')}</p>

          {loading ? (
            <div className="flex justify-center py-8 text-tg-hint">{t('common.loading')}</div>
          ) : filter ? (
            <>
              {/* Gender */}
              <div className="mb-5">
                <p className="mb-2 text-sm font-medium text-tg-hint">{t('filters.showMe')}</p>
                <div className="flex flex-wrap gap-2">
                  {GENDER_OPTION_KEYS.map(opt => (
                    <button
                      key={opt.value}
                      onClick={() => setFilter(f => f ? { ...f, gender: opt.value } : f)}
                      className={`rounded-xl px-4 py-2 text-sm font-medium transition-colors ${
                        filter.gender === opt.value
                          ? 'bg-primary text-white'
                          : 'bg-tg-secondary-bg text-tg-text'
                      }`}
                    >
                      {t(opt.labelKey)}
                    </button>
                  ))}
                </div>
              </div>

              {/* Age range */}
              <div className="mb-5">
                <div className="mb-3 flex items-baseline justify-between">
                  <p className="text-sm font-medium text-tg-hint">{t('filters.ageRange')}</p>
                  <p className="text-sm font-semibold text-tg-text">
                    {filter.age_min} – {filter.age_max}
                  </p>
                </div>
                <RangeSlider
                  min={18}
                  max={100}
                  valueMin={filter.age_min}
                  valueMax={filter.age_max}
                  onChange={(newMin, newMax) =>
                    setFilter(f => f ? { ...f, age_min: newMin, age_max: newMax } : f)
                  }
                />
              </div>

              {/* Distance — admin only */}
              {isAdmin && (
                <div className="mb-6">
                  <p className="mb-2 text-sm font-medium text-tg-hint">
                    {t('filters.distance', {
                      value: filter.radius_km === 0 ? t('filters.unlimited') : `${filter.radius_km} km`,
                    })}
                  </p>
                  <div className="flex flex-wrap gap-2">
                    {RADIUS_PRESETS.map(km => (
                      <button
                        key={km}
                        onClick={() => setFilter(f => f ? { ...f, radius_km: km } : f)}
                        className={`rounded-xl px-4 py-2 text-sm font-medium transition-colors ${
                          filter.radius_km === km
                            ? 'bg-primary text-white'
                            : 'bg-tg-secondary-bg text-tg-text'
                        }`}
                      >
                        {km === 0 ? '∞' : `${km} km`}
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {error && <p className="mb-3 text-sm text-red-500">{error}</p>}

              <button
                onClick={handleSave}
                disabled={saving}
                className="w-full rounded-xl bg-primary py-3 font-medium text-white disabled:opacity-50"
              >
                {saving ? t('onboarding.saving') : t('filters.saveFilters')}
              </button>
            </>
          ) : null}
        </div>
      </motion.div>
    </div>
  )
}
